import type { TopicName } from './components/TopicIcon'

/*
  Which mark belongs against an item on The Table.

  ── Why this is a keyword match and not a model call ────────────────────────

  A model would classify better. It would also mean a column, a migration, a worker change
  and a backfill, and every item already on every owner's table would sit unmarked until
  that backfill ran. This reads the sentence that is already on the screen, so it works on
  the whole history the moment it ships, costs nothing per item, and can be replaced by a
  stored classification later without a single component changing: the components ask for a
  TopicName and do not care where it came from.

  ── The rule that matters ───────────────────────────────────────────────────

  **A wrong mark is worse than a neutral one.** An item about a van carrying the legal
  scales does not just fail to help, it actively misleads, and the owner stops trusting the
  column. So this is deliberately shy: a topic must be named fairly specifically to win, and
  anything that does not match falls back to the neutral question mark, which is honest
  about knowing nothing.

  Order is significance, not alphabet. The first rule that matches wins, so the narrow
  readings sit above the broad ones: "quote" is pricing before "job" is scheduling, and a
  tribunal is legal before it is anything to do with the crew.
*/

/** Whole-word, case-insensitive, tolerant of plurals and possessives. */
function has(text: string, words: string[]): boolean {
  return words.some(w => new RegExp(`\\b${w}(s|es|ed|ing|'s)?\\b`, 'i').test(text))
}

const RULES: { topic: TopicName; words: string[] }[] = [
  // ── Narrow readings first ────────────────────────────────────────────────
  { topic: 'leaving', words: ['quit', 'quitting', 'resign', 'resignation', 'notice period',
      'hand(ed|ing)? in (his|her|their) notice', 'walked out', 'let him go', 'let her go',
      'lay ?off', 'redundan(t|cy)', 'fire (him|her|them)', 'sack'] },
  { topic: 'wages', words: ['raise', 'pay ?rise', 'payrise', 'wage', 'salary', 'overtime',
      'bonus', 'payroll', 'per hour', 'hourly rate', 'a pay'] },
  { topic: 'hiring', words: ['hire', 'hiring', 'recruit', 'apprentice', 'vacancy',
      'new (tech|guy|starter|hire)', 'take on (a|another)', 'job ad', 'advertise the role'] },
  { topic: 'quality', words: ['callback', 'call ?back', 'complaint', 'rework', 'snag',
      'went wrong', 'came back', 'defect', 'botched', 'unhappy customer'] },
  { topic: 'legal', words: ['solicitor', 'lawyer', 'sue', 'sued', 'suing', 'lawsuit',
      'tribunal', 'court', 'legal', 'dispute', 'letter before action', 'small claims'] },
  { topic: 'safety', words: ['accident', 'injur(y|ies|ed)', 'hurt', 'rams', 'risk assessment',
      'near miss', 'hse', 'health and safety', 'ppe'] },
  { topic: 'insurance', words: ['insur(ance|er|ed)', 'liability', 'premium', 'claim',
      'cover(age)?', 'policy renewal', 'underwrit'] },
  { topic: 'tax', words: ['tax', 'vat', 'hmrc', 'irs', 'accountant', 'return',
      'self ?assessment', 'corporation tax', 'write ?off'] },
  { topic: 'invoice', words: ['invoice', 'unpaid', 'owe', 'owes', 'owing', 'receivable',
      'chase (the )?payment', 'not paid', 'debtor', 'net ?\\d+', 'aged debt'] },
  { topic: 'debt', words: ['loan', 'overdraft', 'finance', 'borrow', 'lender', 'repayment',
      'credit line', 'interest rate', 'the bank'] },
  { topic: 'contract', words: ['contract', 'terms', 'agreement', 'sign(ed)? (up|off)',
      'clause', 'small print', 't&cs', 'retention', 'scope of work'] },
  { topic: 'vehicle', words: ['van', 'truck', 'lorry', 'vehicle', 'fleet', 'mot',
      'mileage', 'fuel', 'diesel', 'tyre', 'tire'] },
  { topic: 'tools', words: ['tool', 'kit', 'plant', 'machine', 'equipment', 'compressor',
      'generator', 'ladder', 'stolen'] },
  { topic: 'stock', words: ['material', 'part', 'supplier', 'merchant', 'wholesaler',
      'stock', 'copper', 'timber', 'delivery', 'lead time', 'shortage'] },
  { topic: 'technology', words: ['software', 'crm', 'app', 'system', 'spreadsheet',
      'subscription', 'saas', 'quickbooks', 'xero', 'automat'] },
  { topic: 'scheduling', words: ['schedul', 'diary', 'calendar', 'book(ing|ed)?',
      'capacity', 'backlog', 'behind', 'lead time', 'slot', 'overrun'] },
  { topic: 'reviews', words: ['review', 'google review', 'star', 'referral', 'reputation',
      'word of mouth', 'testimonial', 'yelp', 'trustpilot'] },
  { topic: 'marketing', words: ['market(ing)?', 'advert', 'advertis', 'ad spend', 'lead',
      'facebook', 'instagram', 'google ads', 'flyer', 'website traffic', 'seo', 'sales', 'sell more', 'win more work'] },
  { topic: 'competitor', words: ['competitor', 'competition', 'rival', 'undercut',
      'another (firm|company|outfit)', 'the other (guy|firm|lot)'] },
  { topic: 'growth', words: ['expand', 'expansion', 'grow', 'growth', 'second crew',
      'another van', 'new branch', 'scale', 'open (a|another)'] },
  { topic: 'customer', words: ['customer', 'client', 'account', 'homeowner', 'landlord',
      'tenant', 'the job for'] },
  { topic: 'crew', words: ['crew', 'team', 'staff', 'employee', 'lads', 'engineer',
      'tech', 'morale', 'sick', 'holiday', 'training', 'ticket'] },
  { topic: 'cashflow', words: ['cash ?flow', 'cash', 'runway', 'margin', 'profit',
      'turnover', 'revenue', 'takings', 'bank balance', 'deposit', 'up ?front',
      'stage payment'] },

  // ── Broadest last ────────────────────────────────────────────────────────
  { topic: 'pricing', words: ['price', 'pricing', 'quote', 'quoting', 'charge',
      'rate', 'callout fee', 'day rate', 'estimate', 'discount', 'cheap', 'expensive'] },
]

/**
 * The mark for an item, or the neutral one when nothing matches with confidence.
 *
 * Exported separately from the component so a stored classification can take over later
 * without touching a single caller.
 */
export function topicFor(text: string | null | undefined): TopicName {
  if (!text) return 'question'
  const t = text.slice(0, 400)
  for (const rule of RULES) if (has(t, rule.words)) return rule.topic
  return 'question'
}
